// src/hooks/useUserList.js
import { useReducer, useEffect, useMemo, useCallback, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import UserService from './../services/UserService';

function reducer(state, action) {
  switch (action.type) {
    case 'INIT':
      return { ...state, loading: true, error: null };
    case 'SUCCESS':
      return { users: action.payload, loading: false, error: null };
    case 'FAILURE':
      return { users: [], loading: false, error: action.error };
    default:
      return state;
  }
}

export function useUserList(query, selectedSite, { enabled = true, delay = 300 } = {}) {
  const [state, dispatch] = useReducer(reducer, {
    users: [],
    loading: false,
    error: null,
  });

  // Abort previous request if a new one starts
  const doFetch = useCallback(
    async (q, signal) => {
      dispatch({ type: 'INIT' });
      try {
        // only call if we have a site; choose your own fallback policy
        const siteId = selectedSite ?? null;
        const data = await UserService.getUserList(q, siteId, { signal });
        if (!signal?.aborted) {
          dispatch({ type: 'SUCCESS', payload: data || [] });
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        dispatch({ type: 'FAILURE', error: err });
      }
    },
    [selectedSite] // 👈 important: keep doFetch in sync with site changes
  );

  // Rebuild debounced whenever delay or doFetch changes
  const debouncedFetch = useMemo(() => {
    const debounced = debounce((q, signal) => doFetch(q, signal), delay);
    return debounced;
  }, [doFetch, delay]);

  const prevSiteRef = useRef(selectedSite);

  // Drive fetching from query, enabled and selectedSite changes
  useEffect(() => {
    if (!enabled) {
      // clear list when disabled
      dispatch({ type: 'SUCCESS', payload: [] });
      prevSiteRef.current = selectedSite;
      return;
    }

    if (query.length >= 3) {
      const ctrl = new AbortController();
      const siteChanged = prevSiteRef.current !== selectedSite;

      if (siteChanged) {
        // If the site changed, cancel any pending debounce and fetch immediately
        debouncedFetch.cancel?.();
        doFetch(query, ctrl.signal);
      } else {
        // Normal, debounced fetch for query input
        debouncedFetch(query, ctrl.signal);
      }

      prevSiteRef.current = selectedSite;

      return () => {
        ctrl.abort();
        debouncedFetch.cancel();
      };
    } else {
      dispatch({ type: 'SUCCESS', payload: [] });
      prevSiteRef.current = selectedSite;
    }
  }, [query, enabled, debouncedFetch, selectedSite, doFetch]);

  return state;
}